import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import memoryApi from '../api/memoryApi';
import ClearChatConfirmation from './ClearChatConfirmation';

/**
 * ChatBreadcrumb - Shows project/session navigation with actions
 *
 * - Click project title → go to project page
 * - Click session title → dropdown with rename, delete, move to project
 */
const ChatBreadcrumb = ({
  sidebarOpen,
  sessions = [],
  onRenameSession,
  onDeleteSession
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showDropdown, setShowDropdown] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [sessionData, setSessionData] = useState(null);
  const [projectData, setProjectData] = useState(null);
  const [projects, setProjects] = useState([]);
  const [showMoveMenu, setShowMoveMenu] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [deleteConfirmSession, setDeleteConfirmSession] = useState(null);

  // Extract session ID from URL
  const sessionIdMatch = location.pathname.match(/\/chatty\/([^/]+)/);
  const currentSessionId = sessionIdMatch?.[1];

  // Check if we're on a project page
  const isProjectPage = location.pathname.includes('/projects/');
  // eslint-disable-next-line no-unused-vars
  const projectIdMatch = location.pathname.match(/\/projects\/([^/]+)/);

  // Load session and project data
  useEffect(() => {
    const loadData = async () => {
      // Skip if no session, on project page, or session_id is literally "projects"
      if (!currentSessionId || isProjectPage || currentSessionId === 'projects') {
        setSessionData(null);
        setProjectData(null);
        setIsLoading(false);
        return;
      }

      // Check if session exists in sessions list first (avoid 404 for deleted sessions)
      const sessionInList = sessions?.find(s => s.session_id === currentSessionId);
      if (!sessionInList && sessions?.length > 0) {
        // Session not in list - likely deleted or invalid
        setSessionData(null);
        setProjectData(null);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);

      // If we have session from list with project_id, use it directly
      if (sessionInList?.project_id) {
        setSessionData(sessionInList);
        setEditTitle(sessionInList.title || 'New Chat');
        // Load project info
        try {
          const projectsList = await memoryApi.listProjects();
          const project = projectsList?.find(p => p.project_id === sessionInList.project_id);
          if (project) {
            setProjectData(project);
          }
        } catch (err) {
          // Ignore project loading errors
        }
        setIsLoading(false);
        return;
      }

      // Fetch full session details for sessions without project_id in list
      try {
        const session = await memoryApi.getSession(currentSessionId);
        if (session) {
          setSessionData(session);
          setEditTitle(session.title || 'New Chat');

          // If session has project, load project info
          if (session.project_id) {
            const projectsList = await memoryApi.listProjects();
            const project = projectsList?.find(p => p.project_id === session.project_id);
            if (project) {
              setProjectData(project);
            }
          } else {
            setProjectData(null);
          }
        }
      } catch (err) {
        // Fallback to session from list if API fails
        if (sessionInList) {
          setSessionData(sessionInList);
          setEditTitle(sessionInList.title || 'New Chat');
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [currentSessionId, isProjectPage, sessions]);

  // Load projects when dropdown opens
  useEffect(() => {
    if (!showDropdown) return;

    const loadProjects = async () => {
      try {
        const projectsList = await memoryApi.listProjects();
        console.log('Loaded projects:', projectsList);
        setProjects(projectsList || []);
      } catch (err) {
        console.error('Error loading projects:', err);
      }
    };
    loadProjects();
  }, [showDropdown]);

  // Don't show on project pages or if no session
  // Also don't show if the "session" is actually "projects" (URL parsing edge case)
  if (isProjectPage || !currentSessionId || currentSessionId === 'projects') {
    return null;
  }

  const handleRename = async () => {
    if (editTitle.trim() && editTitle !== sessionData?.title) {
      await onRenameSession?.(currentSessionId, editTitle.trim());
      setSessionData(prev => prev ? { ...prev, title: editTitle.trim() } : null);
    }
    setIsEditing(false);
    setShowDropdown(false);
  };

  const handleDelete = () => {
    setDeleteConfirmSession(currentSessionId);
    setShowDropdown(false);
  };

  const handleDeleteConfirm = async () => {
    if (deleteConfirmSession) {
      await onDeleteSession?.(deleteConfirmSession);
      setDeleteConfirmSession(null);
    }
  };

  const handleMoveToProject = async (projectId) => {
    try {
      // Update session with new project_id (or null to remove from project)
      await memoryApi.updateSessionProject(currentSessionId, projectId);
      setShowMoveMenu(false);
      setShowDropdown(false);

      if (projectId) {
        // Navigate to the project
        navigate(`/chatty/projects/${projectId}`);
      } else {
        // Moved to standalone - stay here
        setProjectData(null);
      }
    } catch (err) {
      console.error('Error moving session:', err);
      alert('Failed to move chat');
    }
  };

  const sessionTitle = sessionData?.title || 'New Chat';

  return (
    <>
    <ClearChatConfirmation
      isOpen={!!deleteConfirmSession}
      onConfirm={handleDeleteConfirm}
      onCancel={() => setDeleteConfirmSession(null)}
    />
    <div
      className="chat-breadcrumb fixed flex items-center gap-2"
      style={{
        top: '68px',
        left: sidebarOpen ? '340px' : '60px',
        transition: 'left 0.3s ease',
        zIndex: 60
      }}
    >
      {/* Skeleton while loading */}
      {isLoading ? (
        <div className="flex items-center gap-2 animate-pulse">
          <div className="h-4 w-20 rounded" style={{ backgroundColor: 'var(--bg-secondary)' }} />
          <span style={{ color: 'var(--text-secondary)' }}>/</span>
          <div className="h-4 w-24 rounded" style={{ backgroundColor: 'var(--bg-secondary)' }} />
        </div>
      ) : (
        <>
          {/* Project link (if in a project) */}
          {projectData && (
        <>
          <button
            onClick={() => navigate(`/chatty/projects/${projectData.project_id}`)}
            className="text-sm font-medium hover:underline truncate max-w-[150px]"
            style={{ color: 'var(--text-secondary)' }}
            title={projectData.title}
          >
            {projectData.title}
          </button>
          <span style={{ color: 'var(--text-secondary)' }}>/</span>
        </>
      )}

      {/* Session title with dropdown */}
      <div className="relative">
        {isEditing ? (
          <input
            type="text"
            value={editTitle}
            onChange={(e) => setEditTitle(e.target.value)}
            onBlur={handleRename}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleRename();
              if (e.key === 'Escape') {
                setEditTitle(sessionData?.title || 'New Chat');
                setIsEditing(false);
              }
            }}
            className="text-sm font-medium px-2 py-1 rounded"
            style={{
              backgroundColor: 'var(--bg-secondary)',
              color: 'var(--text-primary)',
              border: '1px solid var(--border-color)',
              outline: 'none',
              minWidth: '120px'
            }}
            autoFocus
          />
        ) : (
          <button
            onClick={() => setShowDropdown(!showDropdown)}
            className="flex items-center gap-1 text-sm font-medium hover:opacity-80 truncate max-w-[200px]"
            style={{ color: 'var(--text-primary)' }}
            title={sessionTitle}
          >
            <span className="truncate">{sessionTitle}</span>
            <svg className="w-3 h-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        )}

        {/* Dropdown menu */}
        {showDropdown && !isEditing && (
          <>
            <div
              className="fixed inset-0 z-40"
              onClick={() => {
                setShowDropdown(false);
                setShowMoveMenu(false);
              }}
            />
            <div
              className="absolute top-full left-0 mt-1 rounded-lg shadow-lg z-50 min-w-[200px]"
              style={{
                backgroundColor: 'var(--bg-primary)',
                border: '1px solid var(--border-color)'
              }}
            >
              {/* Rename */}
              <button
                onClick={() => {
                  setIsEditing(true);
                  setShowDropdown(false);
                }}
                className="w-full px-3 py-2 text-left text-sm flex items-center gap-2 hover:bg-gray-100 dark:hover:bg-gray-800"
                style={{ color: 'var(--text-primary)' }}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Rename
              </button>

              {/* Move to project - accordion style */}
              <button
                onClick={() => setShowMoveMenu(!showMoveMenu)}
                className="w-full px-3 py-2 text-left text-sm flex items-center justify-between hover:bg-gray-100 dark:hover:bg-gray-800"
                style={{ color: 'var(--text-primary)' }}
              >
                <span className="flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                  </svg>
                  Move to...
                </span>
                <svg
                  className="w-3 h-3 transition-transform"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  style={{ transform: showMoveMenu ? 'rotate(90deg)' : 'rotate(0deg)' }}
                >
                  <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                </svg>
              </button>

              {/* Accordion project list */}
              {showMoveMenu && (
                <div
                  style={{
                    backgroundColor: 'var(--bg-secondary, rgba(255,255,255,0.05))',
                    maxHeight: '150px',
                    overflowY: 'auto'
                  }}
                >
                  {projects.map(project => (
                    <button
                      key={project.project_id}
                      onClick={() => handleMoveToProject(project.project_id)}
                      className={`w-full px-4 py-2 text-left text-sm truncate hover:bg-gray-100 dark:hover:bg-gray-800 ${project.project_id === projectData?.project_id ? 'opacity-50' : ''}`}
                      style={{ color: 'var(--text-primary)' }}
                      title={project.title}
                      disabled={project.project_id === projectData?.project_id}
                    >
                      {project.title}
                      {project.project_id === projectData?.project_id && ' (current)'}
                    </button>
                  ))}
                  {/* Remove from project - show inside accordion if in a project */}
                  {projectData && (
                    <button
                      onClick={() => handleMoveToProject(null)}
                      className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-800"
                      style={{ color: 'var(--text-secondary)', borderTop: '1px solid var(--border-color)' }}
                    >
                      Remove from project
                    </button>
                  )}
                  {projects.length === 0 && !projectData && (
                    <div className="px-4 py-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                      No projects
                    </div>
                  )}
                </div>
              )}

              {/* Divider */}
              <div style={{ borderTop: '1px solid var(--border-color)' }} />

              {/* Delete */}
              <button
                onClick={handleDelete}
                className="w-full px-3 py-2 text-left text-sm flex items-center gap-2 hover:bg-red-50 dark:hover:bg-red-900/20"
                style={{ color: '#ef4444' }}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Delete
              </button>
            </div>
          </>
        )}
      </div>
        </>
      )}
    </div>
    </>
  );
};

export default ChatBreadcrumb;
